import React, { useRef, useCallback } from 'react';
import { FaMusic, FaPause, FaPlay, FaSpinner, FaTrash } from 'react-icons/fa';

const AudioManager = ({
                          description,
                          onDescriptionChange,
                          audioError,
                          audioPreview,
                          audioRef,
                          isPlaying,
                          audioCurrentTime,
                          audioDuration,
                          audioFileName,
                          audioFileSize,
                          isUploadingAudio,
                          onTogglePlayback,
                          onRemoveAudio,
                          onUploadAudio,
                          formatTime
                      }) => {
    const progress = audioDuration > 0 ? (audioCurrentTime / audioDuration) * 100 : 0;
    const isScrubbingRef = useRef(false);
    const progressBarRef = useRef(null);

    // Helper function to get seek ratio from event
    const getSeekRatio = useCallback((e, bar) => {
        const rect = bar.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const ratio = (clientX - rect.left) / rect.width;
        return Math.min(Math.max(ratio, 0), 1);
    }, []);

    // Update audio time based on ratio
    const updateAudioTime = useCallback((ratio) => {
        if (audioRef.current && audioDuration) {
            audioRef.current.currentTime = ratio * audioDuration;
        }
    }, [audioRef, audioDuration]);

    // Handle progress bar click for seeking
    const handleProgressBarClick = useCallback((e) => {
        if (!audioRef.current || !audioDuration || isScrubbingRef.current) return;
        
        const ratio = getSeekRatio(e, e.currentTarget);
        updateAudioTime(ratio);
    }, [audioRef, audioDuration, getSeekRatio, updateAudioTime]);

    // Handle mouse drag
    const handleMouseDown = useCallback((e) => {
        if (!audioRef.current || !audioDuration) return;
        
        e.preventDefault();
        isScrubbingRef.current = true;
        
        const handleMouseMove = (moveEvent) => {
            if (!isScrubbingRef.current || !progressBarRef.current) return;
            const ratio = getSeekRatio(moveEvent, progressBarRef.current);
            updateAudioTime(ratio);
        };
        
        const handleMouseUp = () => {
            isScrubbingRef.current = false;
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        
        // Also seek on initial mousedown
        const ratio = getSeekRatio(e, e.currentTarget);
        updateAudioTime(ratio);
    }, [audioRef, audioDuration, getSeekRatio, updateAudioTime]);

    // Handle touch drag
    const handleTouchStart = useCallback((e) => {
        if (!audioRef.current || !audioDuration) return;
        
        isScrubbingRef.current = true;
        
        const handleTouchMove = (moveEvent) => {
            if (!isScrubbingRef.current || !progressBarRef.current) return;
            moveEvent.preventDefault();
            const ratio = getSeekRatio(moveEvent, progressBarRef.current);
            updateAudioTime(ratio);
        };
        
        const handleTouchEnd = () => {
            isScrubbingRef.current = false;
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
        
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd);
        
        // Also seek on initial touch
        const ratio = getSeekRatio(e, e.currentTarget);
        updateAudioTime(ratio);
    }, [audioRef, audioDuration, getSeekRatio, updateAudioTime]);

    return (
        <div className="form-group mb-4">
            <label htmlFor="post-description" className="form-label">Post Description *</label>
            <textarea
                id="post-description"
                value={description}
                onChange={(event) => onDescriptionChange(event.target.value)}
                placeholder="Enter post description"
                className="form-control"
            />
            <label htmlFor="audio-upload" className="form-label d-flex align-items-center">
                <FaMusic className="me-2" /> Upload Music
            </label>
            <div className="alert alert-info py-2 px-3 mb-2">
                <small>
                    <strong>💡 Tip:</strong> Upload MP3, WAV, OGG, or M4A audio files. Maximum file size is 50MB.
                </small>
            </div>

            {audioError && (
                <div className="alert alert-danger py-2 px-3 mb-2">
                    <small><strong>Error:</strong> {audioError}</small>
                </div>
            )}

            {audioPreview ? (
                <div className="audio-preview-container">
                    {/* Remove button - top right corner */}
                    <button 
                        type="button" 
                        onClick={onRemoveAudio} 
                        className="audio-remove-btn"
                        aria-label="Remove audio"
                    >
                        <FaTrash />
                    </button>

                    {/* Waveform visualization */}
                    <div className="audio-waveform-container">
                        <div className="audio-waveform">
                            <div className="audio-waveform-bars">
                                {Array.from({ length: 60 }, (_, index) => {
                                    // Create more natural height variation
                                    const baseHeight = 30;
                                    const variation = Math.sin(index * 0.5) * 30 + Math.cos(index * 0.3) * 20;
                                    const height = baseHeight + variation;
                                    const isPlayed = (index / 60) * 100 <= progress;
                                    
                                    return (
                                        <div 
                                            key={index} 
                                            className={`waveform-bar ${isPlayed ? 'played' : 'unplayed'} ${isPlaying ? 'animating' : ''}`}
                                            style={{ height: `${Math.max(20, Math.min(80, height))}%` }}
                                        ></div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Playback controls */}
                    <div className="audio-controls">
                        <button 
                            type="button" 
                            onClick={() => onTogglePlayback()} 
                            className="audio-play-btn"
                            aria-label={isPlaying ? 'Pause' : 'Play'}
                        >
                            {isPlaying ? <FaPause /> : <FaPlay />}
                        </button>

                        <div 
                            ref={progressBarRef}
                            className="audio-progress-bar"
                            onClick={handleProgressBarClick}
                            onMouseDown={handleMouseDown}
                            onTouchStart={handleTouchStart}
                        >
                            <div 
                                className="audio-progress-fill" 
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>

                        <div className="audio-time">
                            {formatTime(audioCurrentTime)} / {formatTime(audioDuration)}
                        </div>
                    </div>

                    {/* Track info */}
                    <div className="audio-track-info">
                        <div className="track-name">
                            <FaMusic className="track-icon" />
                            <span>{audioFileName}</span>
                        </div>
                        <div className="track-size">
                            {(audioFileSize / (1024 * 1024)).toFixed(2)} MB
                        </div>
                    </div>
                </div>
            ) : (
                <div className="audio-upload-container">
                    <label
                        className="audio-upload-area d-flex flex-column align-items-center justify-content-center p-5 border border-dashed rounded cursor-pointer"
                        onDragOver={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                        }}
                        onDrop={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            const [file] = event.dataTransfer.files || [];
                            if (file) {
                                onUploadAudio(file);
                            }
                        }}
                    >
                        <input
                            id="audio-upload"
                            type="file"
                            accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/x-m4a"
                            onChange={(event) => {
                                const [file] = event.target.files || [];
                                if (file) {
                                    onUploadAudio(file);
                                }
                            }}
                            className="d-none"
                            disabled={isUploadingAudio}
                            aria-label="Upload audio file"
                        />
                        <FaMusic className="display-4 mb-3 text-primary" />
                        <p className="mb-1 fw-bold">Click to upload or drag and drop</p>
                        <p className="text-muted small mb-0">MP3, WAV, OGG or M4A</p>
                    </label>
                    {isUploadingAudio && (
                        <div className="mt-3 text-center text-primary">
                            <FaSpinner className="spinner me-2" />
                            Uploading audio file...
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AudioManager;
